import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Progress } from '../components/ui/progress';
import {
  Users, Target, BarChart3, CheckCircle2, Clock, AlertTriangle,
  TrendingUp, Calendar, ListTodo, ArrowUpRight, ArrowDownRight,
  RefreshCw, Loader2, User, Briefcase, Phone, Mail, Star
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const ManagementDashboardPage = () => {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [period, setPeriod] = useState('week');
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamMetrics, setTeamMetrics] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);

  const getToken = () => localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${getToken()}` };
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const [teamRes, tasksRes, dealsRes, ticketsRes] = await Promise.all([
        axios.get(`${API}/api/users/team`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/api/operations/tasks/dashboard`, { headers }).catch(() => ({ data: { stats: {}, recent_tasks: [] } })),
        axios.get(`${API}/api/deals`, { headers }).catch(() => ({ data: { deals: [] } })),
        axios.get(`${API}/api/tickets`, { headers }).catch(() => ({ data: { tickets: [] } }))
      ]);

      const team = teamRes.data || [];
      const taskData = tasksRes.data;
      const deals = dealsRes.data?.deals || dealsRes.data || [];
      const tickets = ticketsRes.data?.tickets || ticketsRes.data || [];

      setTeamMembers(team);
      setTasks(taskData.recent_tasks || []);

      // Calculate team metrics
      const totalDeals = deals.length;
      const closedDeals = deals.filter(d => d.stage === 'closed_won').length;
      const closedTickets = tickets.filter(t => t.status === 'resolved' || t.status === 'closed').length;
      const totalTickets = tickets.length;

      setTeamMetrics({
        teamSize: team.length,
        activeTasks: taskData.stats?.total || 0,
        completedTasks: taskData.stats?.completed || 0,
        overdueTasks: taskData.stats?.overdue || 0,
        dealsWon: closedDeals,
        dealWinRate: totalDeals > 0 ? Math.round((closedDeals / totalDeals) * 100) : 0,
        ticketResolutionRate: totalTickets > 0 ? Math.round((closedTickets / totalTickets) * 100) : 0,
        avgResponseTime: '2.4h'
      });

      // Mock pending approvals
      setPendingApprovals([
        { id: 1, type: 'Leave Request', requester: 'John Smith', date: '2026-01-22', urgency: 'medium' },
        { id: 2, type: 'Expense Report', requester: 'Sarah Johnson', amount: '$450', urgency: 'low' },
        { id: 3, type: 'Budget Request', requester: 'Marketing Team', amount: '$5,000', urgency: 'high' }
      ]);

    } catch (err) {
      console.error('Failed to fetch management data:', err);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getInitials = (firstName, lastName) => {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="management-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Briefcase className="h-8 w-8 text-blue-600" />
            Management Dashboard
          </h1>
          <p className="text-muted-foreground mt-1">Team performance and operational metrics</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="week">This Week</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
              <SelectItem value="quarter">This Quarter</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="team">Team Performance</TabsTrigger>
          <TabsTrigger value="approvals">Pending Approvals</TabsTrigger>
          <TabsTrigger value="tasks">Team Tasks</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {/* Key Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Team Size</p>
                    <p className="text-3xl font-bold">{teamMetrics?.teamSize || 0}</p>
                  </div>
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <Users className="h-6 w-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Active Tasks</p>
                    <p className="text-3xl font-bold">{teamMetrics?.activeTasks || 0}</p>
                    <p className="text-xs text-green-600 flex items-center gap-1 mt-1">
                      <CheckCircle2 className="h-3 w-3" />
                      {teamMetrics?.completedTasks || 0} completed
                    </p>
                  </div>
                  <div className="p-3 bg-green-50 rounded-lg">
                    <ListTodo className="h-6 w-6 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Deal Win Rate</p>
                    <p className="text-3xl font-bold">{teamMetrics?.dealWinRate || 0}%</p>
                    <p className="text-xs text-green-600 flex items-center gap-1 mt-1">
                      <ArrowUpRight className="h-3 w-3" />
                      +5% vs last period
                    </p>
                  </div>
                  <div className="p-3 bg-purple-50 rounded-lg">
                    <Target className="h-6 w-6 text-purple-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Ticket Resolution</p>
                    <p className="text-3xl font-bold">{teamMetrics?.ticketResolutionRate || 0}%</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Avg: {teamMetrics?.avgResponseTime}
                    </p>
                  </div>
                  <div className="p-3 bg-orange-50 rounded-lg">
                    <Clock className="h-6 w-6 text-orange-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Overdue Alert */}
          {teamMetrics?.overdueTasks > 0 && (
            <Card className="bg-red-50 border-red-200">
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                  <div>
                    <p className="font-medium text-red-900">Attention Required</p>
                    <p className="text-sm text-red-700">
                      {teamMetrics.overdueTasks} tasks are overdue and need immediate attention.
                    </p>
                  </div>
                  <Button variant="destructive" size="sm" className="ml-auto">
                    View Overdue
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Team & Approvals */}
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Team Overview */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    Team Members
                  </span>
                  <Badge variant="secondary">{teamMembers.length}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[250px]">
                  <div className="space-y-3">
                    {teamMembers.slice(0, 8).map((member) => (
                      <div key={member.id} className="flex items-center justify-between p-2 rounded-lg hover:bg-slate-50">
                        <div className="flex items-center gap-3">
                          <Avatar>
                            <AvatarFallback className="bg-slate-200">
                              {getInitials(member.first_name, member.last_name)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium">{member.first_name} {member.last_name}</p>
                            <p className="text-xs text-muted-foreground capitalize">
                              {member.role?.replace(/_/g, ' ')}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button variant="ghost" size="icon">
                            <Mail className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon">
                            <Phone className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                    {teamMembers.length === 0 && (
                      <p className="text-center text-muted-foreground py-4">No team members found</p>
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>

            {/* Pending Approvals */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5" />
                    Pending Approvals
                  </span>
                  <Badge variant="secondary">{pendingApprovals.length}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {pendingApprovals.map((approval) => (
                    <div key={approval.id} className="flex items-center justify-between p-3 rounded-lg border">
                      <div>
                        <p className="font-medium">{approval.type}</p>
                        <p className="text-sm text-muted-foreground">
                          {approval.requester} {approval.amount && `• ${approval.amount}`}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge 
                          variant={approval.urgency === 'high' ? 'destructive' : approval.urgency === 'medium' ? 'secondary' : 'outline'}
                        >
                          {approval.urgency}
                        </Badge>
                        <Button size="sm" variant="outline">Review</Button>
                      </div>
                    </div>
                  ))}
                  {pendingApprovals.length === 0 && (
                    <p className="text-center text-muted-foreground py-4">No pending approvals</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Team Performance Tab */}
        <TabsContent value="team" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Team Performance Leaderboard</CardTitle>
              <CardDescription>Performance metrics for team members this period</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {teamMembers.map((member, idx) => {
                  // Mock performance data
                  const performance = {
                    tasksCompleted: Math.floor(Math.random() * 20) + 5,
                    dealsWon: Math.floor(Math.random() * 5),
                    ticketsResolved: Math.floor(Math.random() * 15) + 3,
                    rating: (Math.random() * 2 + 3).toFixed(1)
                  };

                  return (
                    <div key={member.id} className="flex items-center gap-4 p-4 rounded-lg border">
                      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-slate-100 font-bold text-slate-600">
                        {idx + 1}
                      </div>
                      <Avatar>
                        <AvatarFallback>{getInitials(member.first_name, member.last_name)}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <p className="font-medium">{member.first_name} {member.last_name}</p>
                        <p className="text-xs text-muted-foreground capitalize">{member.role?.replace(/_/g, ' ')}</p>
                      </div>
                      <div className="flex items-center gap-6 text-sm">
                        <div className="text-center">
                          <p className="font-bold text-green-600">{performance.tasksCompleted}</p>
                          <p className="text-xs text-muted-foreground">Tasks</p>
                        </div>
                        <div className="text-center">
                          <p className="font-bold text-blue-600">{performance.dealsWon}</p>
                          <p className="text-xs text-muted-foreground">Deals</p>
                        </div>
                        <div className="text-center">
                          <p className="font-bold text-purple-600">{performance.ticketsResolved}</p>
                          <p className="text-xs text-muted-foreground">Tickets</p>
                        </div>
                        <div className="flex items-center gap-1">
                          <Star className="h-4 w-4 text-amber-500 fill-amber-500" />
                          <span className="font-medium">{performance.rating}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pending Approvals Tab */}
        <TabsContent value="approvals" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>All Pending Approvals</CardTitle>
              <CardDescription>Items requiring your approval</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {pendingApprovals.map((approval) => (
                  <div key={approval.id} className="p-4 rounded-lg border">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="font-medium text-lg">{approval.type}</p>
                        <p className="text-sm text-muted-foreground">Requested by {approval.requester}</p>
                      </div>
                      <Badge variant={approval.urgency === 'high' ? 'destructive' : 'secondary'}>
                        {approval.urgency} priority
                      </Badge>
                    </div>
                    {approval.amount && (
                      <p className="text-2xl font-bold mb-3">{approval.amount}</p>
                    )}
                    <div className="flex gap-2">
                      <Button variant="default">Approve</Button>
                      <Button variant="outline">Reject</Button>
                      <Button variant="ghost">Request Info</Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Team Tasks Tab */}
        <TabsContent value="tasks" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Recent Team Tasks</CardTitle>
              <CardDescription>Tasks assigned to your team</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {tasks.map((task) => (
                  <div key={task.id} className="flex items-center justify-between p-3 rounded-lg border">
                    <div className="flex items-center gap-3">
                      <div className={`w-2 h-2 rounded-full ${
                        task.priority === 'critical' ? 'bg-red-500' :
                        task.priority === 'high' ? 'bg-orange-500' :
                        task.priority === 'medium' ? 'bg-blue-500' : 'bg-slate-400'
                      }`} />
                      <div>
                        <p className="font-medium">{task.title}</p>
                        <p className="text-xs text-muted-foreground">
                          {task.assigned_to_name || 'Unassigned'} • Due: {task.due_date ? new Date(task.due_date).toLocaleDateString() : 'No due date'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={task.status === 'completed' ? 'default' : 'secondary'}>
                        {task.status}
                      </Badge>
                    </div>
                  </div>
                ))}
                {tasks.length === 0 && (
                  <p className="text-center text-muted-foreground py-8">No recent tasks</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ManagementDashboardPage;
